import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

/**
 * Related destinations module for blog posts.
 *
 * Given the post's tags, finds every country/region whose name matches a tag
 * (case-insensitive) and renders a card grid linking to `/destinations/{id}`.
 * This creates a second layer of internal-linking beyond the inline
 * auto-linker: readers land on the blog article via a long-tail search, then
 * see a strong CTA to the commercial destination page.
 *
 * Renders NOTHING when zero tags match a real destination — avoiding a
 * "Related destinations" heading with an empty grid.
 */
const RelatedDestinations = ({ tags = [], countries = [] }) => {
  const matches = useMemo(() => {
    if (!tags.length || !countries.length) return [];
    const lower = tags.map((t) => (t || '').toLowerCase().trim()).filter(Boolean);
    const found = [];
    const seen = new Set();
    for (const c of countries) {
      if (!c?.name || !c?.id || seen.has(c.id)) continue;
      const nameLower = c.name.toLowerCase();
      if (lower.some((t) => nameLower === t || nameLower.includes(t) || t.includes(nameLower))) {
        found.push(c);
        seen.add(c.id);
      }
    }
    return found.slice(0, 6);
  }, [tags, countries]);

  if (matches.length === 0) return null;

  return (
    <aside
      data-testid="related-destinations"
      className="mt-12 pt-10 border-t border-gray-200 dark:border-neutral-800"
    >
      <h2 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-neutral-100 mb-6">
        Shop eSIMs for this article
      </h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {matches.map((c) => (
          <Link
            key={c.id}
            to={`/destinations/${c.id}`}
            data-testid={`related-destination-${c.id}`}
            className="group flex items-center gap-4 p-4 rounded-2xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:border-black dark:hover:border-white transition-colors"
          >
            {c.flag && (
              <img
                src={c.flag}
                alt={`${c.name} flag`}
                className="w-12 h-8 object-cover rounded shadow-sm shrink-0"
                loading="lazy"
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="font-bold text-gray-900 dark:text-neutral-100 truncate">
                Buy {c.name} eSIM
              </div>
              <div className="text-xs text-gray-500 dark:text-neutral-400">
                Instant activation
              </div>
            </div>
            <ArrowRight
              size={18}
              className="text-gray-400 dark:text-neutral-500 group-hover:text-gray-900 dark:group-hover:text-neutral-100 transition-colors shrink-0"
            />
          </Link>
        ))}
      </div>
    </aside>
  );
};

export default RelatedDestinations;
